/**
 * Plano de Ação — visão do AVALIADOR (time CAINT). É a estrutura do Portal da
 * Unidade (lateral + barra de progresso + setores recolhíveis + o mesmo card)
 * com a camada de revisão por cima. Não é página isolada: as telas interna
 * mobile (`screens/PlanoAcao.tsx`) e desktop (`desktop/screens/PlanoAcao.tsx`)
 * a renderizam dentro das próprias cascas, abaixo do cabeçalho de cada uma.
 *
 * Escopo e filtro são independentes:
 *   - SETOR escolhido na lateral → de onde saem os números (barra, obrigatórios
 *     e os KPIs contam só aquele setor);
 *   - KPI escolhido → quais itens aparecem; cada setor da lateral mostra
 *     quantos itens tem naquela situação ("3 a revisar").
 * Trocar o filtro NÃO troca o setor: senão os KPIs deixariam de ser do setor.
 *
 * A lateral vira coluna única em larguras estreitas por container query (e não
 * por media query): no desktop o menu do app come parte da tela.
 */
import { UNIDADES } from "@caint/data";
import type { PlanoAcao, PlanoItem, RelatorioDTO } from "@caint/shared";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError, apiClient } from "../../lib/api.ts";
import { RelatorioIndisponivel } from "../relatorio/RelatorioIndisponivel.tsx";
import { EmitirRelatorioOverlay } from "../relatorio/RelatorioInstitucional.tsx";
import { LegendaMaturidade } from "./contextoAvaliacao.tsx";
import {
  BarraProgresso,
  FILTROS,
  type FiltroPlano,
  SecoesPorSetor,
  type Totais,
  agruparPorSetor,
  contarFiltro,
  passaFiltro,
  rotuloContagem,
  setorDoItem,
  totaisDeItens,
} from "./planoCompartilhado.tsx";
import { useAviso } from "./useAviso.ts";
import "../../portal/portal.css";

const NOME_UNIDADE = new Map(UNIDADES.map((u) => [u.id, u.nome]));

export interface ResumoEscopo {
  /** Itens do setor escolhido (ou do plano inteiro). */
  escopo: PlanoItem[];
  /** Barra de progresso + obrigatórios, contados só no escopo. */
  totais: Totais;
  /** Número de cada KPI (um por filtro), contado só no escopo. */
  kpis: Record<FiltroPlano, number>;
  /** O que a lista mostra: escopo ∩ filtro. */
  visiveis: PlanoItem[];
}

/** Os números e a lista da tela para um setor (`null` = todos) e um filtro. */
export function resumoDoEscopo(
  itens: readonly PlanoItem[],
  setor: string | null,
  filtro: FiltroPlano,
): ResumoEscopo {
  const escopo = setor ? itens.filter((i) => setorDoItem(i) === setor) : [...itens];
  const kpis = Object.fromEntries(FILTROS.map((f) => [f.id, contarFiltro(escopo, f.id)])) as Record<
    FiltroPlano,
    number
  >;
  return {
    escopo,
    totais: totaisDeItens(escopo),
    kpis,
    visiveis: escopo.filter((i) => passaFiltro(i, filtro)),
  };
}

/** Um setor na lateral: progresso próprio + selo do filtro ativo ("2 a revisar"). */
function BotaoSetor({
  nome,
  itens,
  ativo,
  filtro,
  onClick,
  todos,
}: {
  nome: string;
  itens: readonly PlanoItem[];
  ativo: boolean;
  filtro: FiltroPlano;
  onClick: () => void;
  todos?: boolean;
}) {
  const feitos = itens.filter((i) => i.status === "concluido").length;
  const pct = itens.length ? Math.round((feitos / itens.length) * 100) : 0;
  const comFiltro = filtro !== "todos";
  const n = contarFiltro(itens, filtro);
  const cor = FILTROS.find((f) => f.id === filtro)?.cor;
  return (
    <button
      type="button"
      className={`pav-set${ativo ? " on" : ""}${comFiltro && n === 0 ? " vazio" : ""}${todos ? " todos" : ""}`}
      aria-pressed={ativo}
      onClick={onClick}
    >
      <span className="pav-set-nome">{nome}</span>
      <span className={`pav-set-cont${feitos === itens.length ? " ok" : ""}`}>
        {feitos}/{itens.length}
      </span>
      <span className="pav-set-bar" aria-hidden="true">
        <i style={{ width: `${pct}%` }} />
      </span>
      {comFiltro && n > 0 && (
        <span className={`pav-set-flag${cor ? ` ${cor}` : ""}`}>{rotuloContagem(filtro, n)}</span>
      )}
    </button>
  );
}

export function PlanoAvaliador({ avaliacaoId, token }: { avaliacaoId: string | null; token: string | null }) {
  const [plano, setPlano] = useState<PlanoAcao | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [setorSel, setSetorSel] = useState<string | null>(null);
  const [filtro, setFiltro] = useState<FiltroPlano>("todos");
  const { aviso, notificar } = useAviso();

  // Relatório Preliminar (PDF): só o backend decide se está liberado (409 ⇒ não).
  const [relatorio, setRelatorio] = useState<RelatorioDTO | null>(null);
  const [relIndisponivel, setRelIndisponivel] = useState(false);
  const [emitindo, setEmitindo] = useState(false);

  const carregar = useCallback(async () => {
    if (!avaliacaoId || !token) {
      setCarregando(false);
      return;
    }
    setCarregando(true);
    setErro(null);
    try {
      setPlano(await apiClient.planoDaAvaliacao(avaliacaoId, token));
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Falha ao carregar o plano");
    } finally {
      setCarregando(false);
    }
  }, [avaliacaoId, token]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const regerar = async () => {
    if (!avaliacaoId || !token) {
      return;
    }
    setOcupado(true);
    setErro(null);
    try {
      setPlano(await apiClient.regerarPlano(avaliacaoId, token));
      notificar("Plano regerado do checklist ✓");
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Falha ao regerar");
    } finally {
      setOcupado(false);
    }
  };

  const emitirRelatorio = async () => {
    if (!avaliacaoId || !token) {
      return;
    }
    setEmitindo(true);
    try {
      setRelatorio(await apiClient.relatorioDaAvaliacao(avaliacaoId, token));
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setRelIndisponivel(true); // avaliação ainda não validada → mensagem amigável
        return;
      }
      notificar("Falha ao emitir o relatório.");
    } finally {
      setEmitindo(false);
    }
  };

  const aoSalvarItem = (atualizado: PlanoItem) => {
    setPlano((prev) =>
      prev ? { ...prev, itens: prev.itens.map((i) => (i.id === atualizado.id ? atualizado : i)) } : prev,
    );
  };

  const itens = plano?.itens ?? [];
  const grupos = useMemo(() => agruparPorSetor(plano?.itens ?? []), [plano]);
  // Setor que deixou de existir (ex.: depois de regerar) volta para "todos".
  const setor = setorSel && grupos.some((g) => g.setor === setorSel) ? setorSel : null;
  const { totais, kpis, visiveis } = useMemo(
    () => resumoDoEscopo(plano?.itens ?? [], setor, filtro),
    [plano, setor, filtro],
  );
  const nomeSetor = setor ? (grupos.find((g) => g.setor === setor)?.nome ?? setor) : "Todos os setores";
  const rotuloFiltro = FILTROS.find((f) => f.id === filtro)?.rotulo ?? "";
  const noFiltroNoPlano = contarFiltro(itens, filtro);
  const unidadeNome = plano ? (NOME_UNIDADE.get(plano.unidadeId) ?? plano.unidadeId) : "";

  return (
    <div className="pav">
      {!avaliacaoId && <p className="pl-erro">Selecione um ciclo/unidade para ver o plano.</p>}
      {erro && (
        <p role="alert" className="pl-erro">
          {erro}
        </p>
      )}
      {carregando && (
        <div className="pl-carregando">
          <span className="pl-spinner" aria-hidden="true" />
          Carregando o plano…
        </div>
      )}

      {plano && itens.length === 0 && !carregando && (
        <div className="pl-vazio">
          <h2>Nenhum item-ação ainda</h2>
          <p>Marque itens em maturidade 0–2 no checklist e sincronize; depois, regere o plano.</p>
          <button
            type="button"
            className="pl-btn pl-btn-sec pav-vazio-btn"
            disabled={ocupado}
            onClick={() => void regerar()}
          >
            {ocupado ? "Processando…" : "Regerar do checklist"}
          </button>
        </div>
      )}

      {itens.length > 0 && token && (
        <div className="pav-shell">
          <aside className="pav-side" aria-label="Setores e avaliação">
            <section className="pl-side-card pav-setores">
              <div className="pl-side-h">Setores</div>
              <div className="pav-setlist">
                <BotaoSetor
                  nome="Todos os setores"
                  itens={itens}
                  ativo={setor === null}
                  filtro={filtro}
                  onClick={() => setSetorSel(null)}
                  todos
                />
                {grupos.map((g) => (
                  <BotaoSetor
                    key={g.setor}
                    nome={g.nome}
                    itens={g.itens}
                    ativo={setor === g.setor}
                    filtro={filtro}
                    onClick={() => setSetorSel(g.setor)}
                  />
                ))}
              </div>
            </section>

            <section className="pl-side-card pav-info">
              <div className="pl-side-h">Avaliação</div>
              <div className="pl-ident-nome">{unidadeNome}</div>
              <div className="pl-ident-row">
                <span>Itens</span> {itens.length} achados · nota 0–2
              </div>
              <div className="pl-ident-row">
                <span>Setores</span> {grupos.length}
              </div>
              <div className="pav-info-acoes">
                <button
                  type="button"
                  className="pl-btn pl-btn-sec pav-info-btn"
                  disabled={emitindo}
                  onClick={() => void emitirRelatorio()}
                >
                  {emitindo ? "Gerando…" : "Emitir relatório (PDF)"}
                </button>
                <button
                  type="button"
                  className="pl-btn pl-btn-sec pav-info-btn"
                  disabled={ocupado}
                  onClick={() => void regerar()}
                >
                  {ocupado ? "Processando…" : "Regerar do checklist"}
                  <small>Traz achados novos; não apaga respostas</small>
                </button>
              </div>
            </section>
          </aside>

          <div className="pav-prog">
            <BarraProgresso
              totais={totais}
              topo={
                <div className="pav-escopo">
                  <span className="pav-escopo-lbl">Mostrando</span>
                  <span className="pav-escopo-nome">{nomeSetor}</span>
                  {setor && (
                    <button type="button" className="pav-escopo-x" onClick={() => setSetorSel(null)}>
                      Ver todos os setores ✕
                    </button>
                  )}
                </div>
              }
            >
              <fieldset className="pav-kpis" aria-label="Filtrar por situação">
                {FILTROS.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    className={`pl-stat${f.cor ? ` ${f.cor}` : ""}${filtro === f.id ? " on" : ""}`}
                    aria-pressed={filtro === f.id}
                    onClick={() => setFiltro(f.id)}
                  >
                    <span className="pl-stat-num">{kpis[f.id]}</span>
                    <span className="pl-stat-lbl">{f.rotulo}</span>
                  </button>
                ))}
              </fieldset>
            </BarraProgresso>
          </div>

          <div className="pav-main">
            <LegendaMaturidade />
            {visiveis.length === 0 ? (
              <div className="pav-vazio">
                <p>
                  Nenhum item
                  {filtro !== "todos" && (
                    <>
                      {" "}
                      em <b>{rotuloFiltro}</b>
                    </>
                  )}
                  {setor && (
                    <>
                      {" "}
                      em <b>{nomeSetor}</b>
                    </>
                  )}
                  .
                </p>
                {setor && noFiltroNoPlano > 0 && (
                  <button
                    type="button"
                    className="pl-btn pl-btn-sec pav-vazio-btn"
                    onClick={() => setSetorSel(null)}
                  >
                    Ver em todos os setores ({noFiltroNoPlano})
                  </button>
                )}
              </div>
            ) : (
              // `key`: trocar setor ou filtro reabre no 1º item a revisar do novo recorte.
              <SecoesPorSetor
                key={`${setor ?? "*"}|${filtro}`}
                itens={visiveis}
                universo={itens}
                token={token}
                onSalvo={aoSalvarItem}
                onNotificar={notificar}
                fatoEditavel
                mostrarCodigo
                mostrarContextoAvaliacao
              />
            )}
          </div>
        </div>
      )}

      {aviso && <output className="pl-toast">{aviso}</output>}

      {relatorio && <EmitirRelatorioOverlay dto={relatorio} onFechar={() => setRelatorio(null)} />}
      {relIndisponivel && (
        <div className="ri-overlay">
          <div className="ri-toolbar no-print">
            <button type="button" className="m-btn" onClick={() => setRelIndisponivel(false)}>
              Fechar
            </button>
          </div>
          <RelatorioIndisponivel contexto="interno" />
        </div>
      )}
    </div>
  );
}
